if (!customElements.get('cross-sell-form')) {
  customElements.define(
    'cross-sell-form',
    class CrossSellForm extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      get context() {
        return this.dataset.context || 'pdp';
      }

      // Resolve the template-instance id Shopify expects in the sections API.
      // The cart template (templates/cart.json) registers `main-cart-footer.liquid`
      // under the id `cart-footer` (and `main-cart-items.liquid` under `cart-items`),
      // and those template ids are what get printed into the `data-id` attribute via
      // `{{ section.id }}`. Asking the sections API for the bare file name renders
      // the section STANDALONE — without the template's blocks — which for
      // main-cart-footer omits the entire `{% when 'subtotal' %}` branch (i.e. the
      // `.js-contents` totals subtree we need to swap). Reading the value off the DOM
      // matches what `cart.js`'s qty/remove flow does, so we behave identically.
      getCartPageSectionIds() {
        const itemsEl = document.getElementById('main-cart-items');
        const footerEl = document.getElementById('main-cart-footer');
        return {
          items: (itemsEl && itemsEl.dataset.id) || 'main-cart-items',
          footer: (footerEl && footerEl.dataset.id) || 'main-cart-footer',
        };
      }

      getSections() {
        if (this.context === 'page') {
          const ids = this.getCartPageSectionIds();
          return [ids.items, ids.footer, 'cart-cross-sell-page', 'cart-icon-bubble'];
        }
        // 'drawer' and 'pdp' both need the drawer sections
        return ['cart-drawer', 'cart-cross-sell-drawer', 'cart-icon-bubble'];
      }

      onSubmit(e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        const btn = this.querySelector('[type="submit"]');
        if (!btn || btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('loading')) return;

        const variantId = this.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const sections = this.getSections();
        const sectionsUrl = window.routes?.cart_url || window.location.pathname;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1, sections, sections_url: sectionsUrl }),
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) throw new Error(data.description || 'Unable to add item');
            this.renderResponse(data);
          })
          .catch((err) => {
            console.error('CrossSellForm error:', err);
            // Re-enabled in .finally() below — a failed add (sold out, network
            // blip) shouldn't permanently brick the button; the shopper can retry.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cross-sell-form',
                productVariantId: variantId,
                message: err.message,
              });
            }
          })
          .finally(() => {
            btn.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            btn.removeAttribute('aria-disabled');
          });
      }

      parse(html) {
        return new DOMParser().parseFromString(html, 'text/html');
      }

      renderResponse(data) {
        const sections = data.sections || {};

        // Update cart icon bubble (all contexts)
        if (sections['cart-icon-bubble']) {
          const src = this.parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
          const dest = document.querySelector('#cart-icon-bubble');
          if (src && dest) dest.innerHTML = src.innerHTML;
        }

        if (this.context === 'page') {
          this.renderCartPage(sections);
        } else if (this.context === 'pdp') {
          this.renderPDP(sections);
        } else {
          this.renderDrawer(sections);
        }

        // Notify the rest of the page that the cart changed. Runs for ALL
        // contexts (page / drawer / pdp) because both event channels have
        // listeners that can sit on any of these surfaces:
        //
        //   1. `PUB_SUB_EVENTS.cartUpdate` is the theme's internal channel
        //      (constants.js + pubsub.js). Payload mirrors `product-form.js`
        //      so existing subscribers (quick-add-bulk, product-info,
        //      price-per-item, product-quantity-input) handle this identically
        //      to a PDP "Add to cart". Sourced as 'cart-items' so cart.js's
        //      <cart-items>/<cart-drawer-items> subscribers skip the redundant
        //      section re-fetch — we already rendered them from this response.
        //
        //   2. The DOM `cart:updated` event has two listeners:
        //      a) `snippets/delivery-calculator-rj.liquid` re-quotes shipping
        //         against the new cart. That snippet is rendered both on the
        //         cart page (main-cart-footer) and on PDPs (pdp-drawers-rj),
        //         and it self-guards against duplicate listeners and the
        //         absence of #rates-table — safe to dispatch unconditionally.
        //         It ignores the detail payload.
        //      b) `cart.js` patches the loyalty savings banners
        //         (`.loyalty-cart-page-banner` / `.loyalty-cart-banner`). Both
        //         banners sit OUTSIDE the regions renderCartPage/renderDrawer
        //         replace — the page banner is above `form.cart__contents`
        //         (i.e. outside `#main-cart-items .js-contents`) and the drawer
        //         banner is a sibling of `.drawer__header`, not part of
        //         `cart-drawer-items` or `.cart-drawer__footer`. So they would
        //         keep showing pre-ATC savings unless that listener runs.
        //         It REQUIRES `detail.source === 'cart-items'` and reads the
        //         section HTML out of `detail.cartData.sections` — dispatching
        //         this event bare makes it bail out and the banners go stale.
        //         (The 'pdp' context doesn't depend on this: renderContents()
        //         swaps all of `#CartDrawer`, banner included.)
        const cartUpdateDetail = { source: 'cart-items', cartData: data };
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, cartUpdateDetail);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartUpdateDetail }));
      }

      renderDrawer(sections) {
        if (sections['cart-drawer']) {
          const html = this.parse(sections['cart-drawer']);
          // `.drawer__heading` (the "Your Cart : N Item(s)" title) sits outside the items
          // and footer regions in the drawer template, so it has to be in this list or it
          // would never refresh after a drawer cross-sell ATC.
          ['cart-drawer-items', '.cart-drawer__footer', '.drawer__heading'].forEach((sel) => {
            const src = html.querySelector(sel);
            const dest = document.querySelector(sel);
            if (src && dest) dest.replaceWith(src);
            else if (dest && !src) dest.remove();
          });
        }
        this.injectCrossSellDrawer(sections['cart-cross-sell-drawer']);
      }

      renderPDP(sections) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        // renderContents updates the drawer HTML synchronously and schedules open() via setTimeout.
        // Injecting the cross-sell synchronously after means open() finds the container
        // already populated and skips the separate loadCrossSell() fetch.
        cartDrawer.renderContents({
          sections,
          id: parseInt(this.querySelector('[name="id"]')?.value),
        });
        this.injectCrossSellDrawer(sections['cart-cross-sell-drawer']);
      }

      renderCartPage(sections) {
        const ids = this.getCartPageSectionIds();

        if (sections[ids.items]) {
          const html = this.parse(sections[ids.items]);
          const src = html.querySelector('.js-contents');
          const dest = document.querySelector('#main-cart-items .js-contents');
          if (src && dest) dest.innerHTML = src.innerHTML;
        }
        // Refresh the totals/discount/shipping/total/checkout block.
        // `main-cart-footer` is its own section with its own `.js-contents`;
        // it's reparented into the sidebar at runtime by `main-cart-items.liquid`
        // but the id (`#main-cart-footer`) and inner selector stay the same.
        if (sections[ids.footer]) {
          const html = this.parse(sections[ids.footer]);
          const src = html.querySelector('.js-contents');
          const dest = document.querySelector('#main-cart-footer .js-contents');
          if (src && dest) dest.innerHTML = src.innerHTML;
        }
        if (sections['cart-cross-sell-page']) {
          const target = document.querySelector('#CartPage-CrossSell');
          if (target) {
            const src = this.parse(sections['cart-cross-sell-page']).querySelector('.shopify-section');
            if (src) target.innerHTML = src.innerHTML;
            else target.innerHTML = '';
          }
        }
      }

      injectCrossSellDrawer(sectionHtml) {
        if (!sectionHtml) return;
        const target = document.querySelector('#CartDrawer-CrossSell');
        if (!target) return;
        const src = this.parse(sectionHtml).querySelector('.shopify-section');
        if (src) target.innerHTML = src.innerHTML;
        else target.innerHTML = '';
      }
    }
  );
}
